/**
 * Search-term highlighting (spec §9 "Highlighting"): free-text words of length ≥ 2, matched
 * case-insensitively; overlapping ranges merged; output as segments for `<mark>` rendering.
 */
import { parseQuery } from './grammar';

export interface Segment {
  s: string;
  hit: boolean;
}

/** Free-text words (≥ 2 chars) of a query. */
export function searchTerms(q: string): string[] {
  return parseQuery(q)
    .text.split(/\s+/)
    .map((w) => w.trim())
    .filter((w) => w.length >= 2);
}

/** Merged `[start, end)` ranges of every term occurrence in `text`. */
export function matchRanges(text: string, terms: readonly string[]): [number, number][] {
  const hay = text.toLowerCase();
  const found: [number, number][] = [];
  for (const term of terms) {
    const t = term.toLowerCase();
    if (!t) continue;
    let i = hay.indexOf(t);
    while (i !== -1) {
      found.push([i, i + t.length]);
      i = hay.indexOf(t, i + t.length);
    }
  }
  found.sort((a, b) => a[0] - b[0]);
  const merged: [number, number][] = [];
  for (const r of found) {
    const last = merged[merged.length - 1];
    if (last && r[0] <= last[1]) last[1] = Math.max(last[1], r[1]);
    else merged.push([r[0], r[1]]);
  }
  return merged;
}

/** Split `text` into hit / non-hit segments. */
export function highlightSegments(text: string, terms: readonly string[]): Segment[] {
  if (!text || !terms.length) return [{ s: text, hit: false }];
  const ranges = matchRanges(text, terms);
  if (!ranges.length) return [{ s: text, hit: false }];
  const out: Segment[] = [];
  let pos = 0;
  for (const [a, b] of ranges) {
    if (a > pos) out.push({ s: text.slice(pos, a), hit: false });
    out.push({ s: text.slice(a, b), hit: true });
    pos = b;
  }
  if (pos < text.length) out.push({ s: text.slice(pos), hit: false });
  return out;
}
